import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { audit, catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuditPlansActions } from './audit-plans.actions';
import { AuditPlansService } from '../../service/audit-plans.service';
import { AdministrationService } from '../../service/administration.service';


@Injectable()
export class AuditPlansEffects {
  // I messaggi di errore del BE (GraphQL errors) non arrivavano all'utente:
  // qui vengono mostrati in toast per tutte le operazioni di scrittura.
  handleErrors$ = createEffect(
    (messageService: MessageService = inject(MessageService)) => {
      return this.actions$.pipe(
        ofType(
          AuditPlansActions.createAuditPlanFailure,
          AuditPlansActions.updateAuditPlanFailure,
          AuditPlansActions.deleteAuditPlanFailure,
          AuditPlansActions.associateAuditPlanToProposalsFailure,
          AuditPlansActions.downloadAuditPlansFailure
        ),
        tap(({ error }) => {
          const detail = extractErrorMessage(error);
          if (!detail) return;
          messageService.add({
            severity: 'error',
            summary: 'Error',
            detail,
            life: 5000,
            closable: true
          });
        })
      );
    },
    { functional: true, dispatch: false }
  );

  loadAuditPlans$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(

        ofType(AuditPlansActions.loadAuditPlans),
        mergeMap(({ parameters, fields, year, legalEntityIds }) =>
          rs.getAuditPlans(parameters, year, legalEntityIds).pipe(
            map((auditPlans) => {
              return AuditPlansActions.loadAuditPlansSuccess({
                auditPlans,
                fields
              });
            }),
            catchError((error) => of(AuditPlansActions.loadAuditPlansFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllAuditPlans$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(

        ofType(AuditPlansActions.loadAllAuditPlans),
        mergeMap(({ parameters, fields, year, legalEntityIds }) =>
          rs.getAuditPlans(parameters, year, legalEntityIds).pipe(
            map((auditPlans) => {
              return AuditPlansActions.loadAllAuditPlansSuccess({
                auditPlans,
                fields
              });
            }),
            catchError((error) => of(AuditPlansActions.loadAllAuditPlansFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAuditPlansFilters$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(

        ofType(AuditPlansActions.loadAuditPlansFilters),
        mergeMap(({ legalEntityIds, year }) =>
          rs.getAuditPlansFilters(legalEntityIds, year).pipe(
            map((filters) => {
              return AuditPlansActions.loadAuditPlansFiltersSuccess({
                filters
              });
            }),
            catchError((error) => of(AuditPlansActions.loadAuditPlansFiltersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  createAuditPlan$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.createAuditPlan),
        mergeMap(({ request, legalEntityIds, parameters, parametersAll, proposalsIds, year }) =>
          rs.createAuditPlan(request).pipe(
            mergeMap((auditPlans) => {

              if (proposalsIds.length > 0) {
                return [
                  AuditPlansActions.createAuditPlanSuccess({ auditPlans }),
                  AuditPlansActions.loadAuditPlansFilters({ legalEntityIds, year }),
                  AuditPlansActions.loadAuditPlans({ year: request?.year, parameters, legalEntityIds }),
                  AuditPlansActions.associateAuditPlanToProposals({ auditPlanId: Number(auditPlans?.id), proposalIds: proposalsIds as number[], year: request?.year, parameters, legalEntityIds })
                ]
              } else {
                return [
                  AuditPlansActions.createAuditPlanSuccess({ auditPlans }),
                  AuditPlansActions.loadAuditPlansFilters({ legalEntityIds, year }),
                  AuditPlansActions.loadAuditPlans({ year: request?.year, parameters, legalEntityIds })
                ]
              }
            }
            ),
            catchError((error) =>
              of(AuditPlansActions.createAuditPlanFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  deleteAuditPlan$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.deleteAuditPlan),
        mergeMap(({ id, parameters, parametersAll, year, legalEntityIds }) =>
          rs.deleteAuditPlan(id).pipe(
            mergeMap((auditPlans) => [
              AuditPlansActions.loadAuditPlansFilters({ legalEntityIds, year }),
              AuditPlansActions.loadAuditPlans({ year, parameters, legalEntityIds })
            ]),
            catchError((error) =>
              of(AuditPlansActions.deleteAuditPlanFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateAuditPlan$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.updateAuditPlan),
        mergeMap(({ auditPlan, parameters, parametersAll, year, legalEntityIds, field }) =>
          rs.updateAuditPlan(auditPlan).pipe(
            mergeMap((update) => {
              // Il BE restituisce errori di validazione in `errors` invece di lanciare un GraphQL error
              const errors = (update?.errors ?? []).filter(Boolean);
              if (errors.length > 0) {
                return [AuditPlansActions.updateAuditPlanFailure({ error: { message: errors.join(' - ') }, id: auditPlan.id, field })];
              }
              return [
                AuditPlansActions.loadAuditPlansFilters({ legalEntityIds, year }),
                AuditPlansActions.updateAuditPlanSuccess({ auditPlans: update })
              ];
            }),
            catchError((error) =>
              of(AuditPlansActions.updateAuditPlanFailure({ error, id: auditPlan.id, field }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  getLegalEntities$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.loadLegalEntities),
        mergeMap(({ year }) =>
          rs.getLegalEntities(year).pipe(
            mergeMap((legals) => [
              AuditPlansActions.loadLegalEntitiesSuccess({ legals })
            ]),
            catchError((error) =>
              of(AuditPlansActions.loadLegalEntitiesFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  associateAuditPlanToProposals$ = createEffect(
    (rs: AuditPlansService = inject(AuditPlansService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.associateAuditPlanToProposals),
        mergeMap(({ auditPlanId, proposalIds, legalEntityIds, year, fields, parameters }) =>
          rs.associateAuditPlanToProposals(auditPlanId, proposalIds).pipe(
            mergeMap((association) => [
              // AuditPlansActions.associateAuditPlanToProposalsSuccess({ association })
              AuditPlansActions.loadAuditPlans({ legalEntityIds, year, fields, parameters })
            ]),
            catchError((error) =>
              of(AuditPlansActions.associateAuditPlanToProposalsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  exportAuditPlans$ = createEffect(
    (usService: AuditPlansService = inject(AuditPlansService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(AuditPlansActions.downloadAuditPlans),
        switchMap(({ parameters, timeZone, year }) =>
          usService.downloadAuditPlans(timeZone, year, parameters).pipe(
            map((exportAuditPlans) => {
              as.downloadFile(
                exportAuditPlans!.data![0]!.content,
                exportAuditPlans!.data![0]!.filename!
              );
              return AuditPlansActions.downloadAuditPlansSuccess({
                export: exportAuditPlans
              });
            }),
            catchError((error) =>
              of(AuditPlansActions.downloadAuditPlansFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  constructor(private actions$: Actions) { }
}

/**
 * Estrae il messaggio parlante restituito dal BE da un ApolloError.
 * Ritorna null per gli errori FORBIDDEN, gia' gestiti da RequestInterceptor,
 * per non mostrare due toast identici.
 */
function extractErrorMessage(error: any): string | null {
  const graphQLErrors: any[] = error?.graphQLErrors ?? [];

  if (graphQLErrors.length > 0) {
    if (graphQLErrors.every(err => err?.extensions?.classification === 'FORBIDDEN')) {
      return null;
    }
    const messages = graphQLErrors
      .filter(err => err?.extensions?.classification !== 'FORBIDDEN')
      .map(err => err?.message)
      .filter(Boolean);
    if (messages.length > 0) return messages.join(' - ');
  }

  return error?.message || 'Unexpected error, please try again.';
}
